import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Sets the browser document title with i18n support and dynamic interpolation
 *
 * Automatically appends "- BLIKS IO" to the translated title.
 * Resets to default "BLIKS IO" on unmount.
 *
 * @param translationKey - i18n key for the page title (e.g., 'pageTitles.myTasks')
 * @param interpolationValues - Optional values for i18n interpolation (e.g., { count: 5, name: 'Process Name' })
 *
 * @example
 * ```tsx
 * // Simple static title
 * function LoginPage() {
 *   useDocumentTitle('pageTitles.login');
 *   return <div>Login Form</div>;
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Title with dynamic count
 * function MyTasksPage() {
 *   const { data } = useQuery(['tasks']);
 *   useDocumentTitle('pageTitles.myTasks', { count: data?.length || 0 });
 *   return <div>My Tasks</div>;
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Title with entity name
 * function ProcessDetailPage() {
 *   const { data: process } = useQuery(['process', processId]);
 *   useDocumentTitle('pageTitles.processDetail', { name: process?.name || 'Loading...' });
 *   return <div>Process Details</div>;
 * }
 * ```
 */
export function useDocumentTitle(
  translationKey: string,
  interpolationValues?: Record<string, string | number>
) {
  const { t } = useTranslation();

  useEffect(
    function syncDocumentTitle() {
      // Get the translated title
      const pageTitle = t(translationKey, interpolationValues);

      // Set document title with "- BLIKS IO" suffix
      document.title = `${pageTitle} - BLIKS IO`;

      // Reset to default on unmount
      return () => {
        document.title = 'BLIKS IO';
      };
    },
    [t, translationKey, interpolationValues]
  );
}
